import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faScrewdriverWrench } from '@fortawesome/free-solid-svg-icons';
import { CheckIcon } from '@heroicons/react/24/outline';
import {
  KONSTRUKCJE, MOC_ODNIESIENIA_W, kosztKonstrukcji, obrazekKonstrukcji,
} from '../../lib/konstrukcja';
import { useTranslation } from 'react-i18next';
import { KRAJ } from '../../kraj';

/**
 * Ostatni krok asystenta: na czym stanie instalacja.
 *
 * PO CO PYTAMY: konstrukcja to pozycja, o której przy porównywaniu ofert nikt nie
 * myśli, a różnica między pokryciami jest ogromna — blacha trapezowa ok. 229 zł/kWp
 * wobec dachówki ceramicznej ok. 356 zł/kWp. Przy 8 kWp to ponad tysiąc złotych
 * różnicy wynikającej wyłącznie z tego, co jest na dachu.
 *
 * DOMYŚLNIE BLACHA TRAPEZOWA: najczęstsze pokrycie pod instalacje w Polsce i zarazem
 * najtańsze w montażu. Wybór domyślny ma być tym, który trafia najczęściej, a nie
 * bezpiecznym środkiem — kto ma dachówkę, i tak ją tu zaznaczy.
 *
 * MOC MODUŁU: dopóki użytkownik nie przypnie konkretnego modułu, liczymy dla mocy
 * odniesienia z zestawienia (415 W). Podstawianie mocy z dymka byłoby ładniejsze,
 * ale kwota skakałaby przy każdym odświeżeniu propozycji, zanim ktokolwiek cokolwiek
 * wybrał.
 */
const KrokKonstrukcji = ({ kwp, mocModuluW, wybrana, onZmiana }) => {
  const { t } = useTranslation('asystent');
  const wynik = kosztKonstrukcji({ kwp, konstrukcja: wybrana, mocModuluW });

  return (
    <div className="p-4 md:p-5">
      <div className="flex items-center gap-2 mb-1">
        <FontAwesomeIcon icon={faScrewdriverWrench} className="text-blue-500 text-sm" />
        <h3 className="text-sm font-bold text-gray-900">{t('konstrukcja.naglowek')}</h3>
        <span className="text-[11px] px-2 py-0.5 rounded-md bg-gray-100 text-gray-500">
          {t('konstrukcja.przyblizona')}
        </span>
      </div>
      <p className="text-xs text-gray-500 mb-4">
        {t('konstrukcja.pytanie')}
      </p>

      <div className="grid lg:grid-cols-[minmax(0,1fr),220px] gap-4 items-start">
        <fieldset className="min-w-0">
          <legend className="mb-2 block text-xs font-medium text-gray-600">{t('konstrukcja.rodzaj_polaci')}</legend>
          {/* Kafelki z obrazkami (te same co przy zestawie w karcie klienta, `public/img/konstrukcje/`) zamiast listy
              przeglądarki (prośba właściciela 2026-10-12): rodzaj dachu łatwiej rozpoznać po wyglądzie niż po nazwie. */}
          <div role="radiogroup" aria-label={t('konstrukcja.rodzaj_polaci')} className="grid grid-cols-3 gap-2 sm:grid-cols-5">
            {KONSTRUKCJE.map((k) => {
              const zaznaczona = k.key === wybrana
              const nazwa = t(`konstrukcja.rodzaj.${k.key}`, { defaultValue: k.label })
              return (
                <button key={k.key} type="button" role="radio" aria-checked={zaznaczona} onClick={() => onZmiana(k.key)}
                        className={`group relative flex flex-col items-center gap-1.5 rounded-xl border bg-white p-1.5 pb-2 text-center transition
                                    focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${zaznaczona
                                      ? 'border-blue-500 ring-1 ring-blue-500'
                                      : 'border-gray-200 hover:border-blue-300 hover:shadow-sm'}`}>
                  <img src={obrazekKonstrukcji(k.key)} alt="" loading="lazy"
                       className="aspect-square w-full rounded-lg bg-gray-50 object-cover" />
                  <span className={`text-[11px] leading-tight ${zaznaczona ? 'font-semibold text-blue-800' : 'text-gray-700'}`}>{nazwa}</span>
                  {zaznaczona && (
                    <span className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 text-white shadow">
                      <CheckIcon className="h-3.5 w-3.5" strokeWidth={3} aria-hidden="true" />
                    </span>
                  )}
                </button>
              )
            })}
          </div>
          <span className="mt-2 block text-[11px] text-gray-400">
            {t('konstrukcja.pionowy')}
            {' '}
            {mocModuluW
              ? t('konstrukcja.modul', { w: mocModuluW })
              : t('konstrukcja.modul_odniesienia', { w: MOC_ODNIESIENIA_W })}
          </span>
        </fieldset>

        {wynik ? (
          <div className="rounded-xl border border-blue-200 bg-blue-50/60 px-4 py-3">
            <p className="text-[11px] text-blue-700">{t('konstrukcja.koszt')}</p>
            <p className="text-2xl font-bold text-blue-900 tabular-nums leading-tight">
              {wynik.razemZl.toLocaleString(KRAJ.locale)} {KRAJ.symbol}
            </p>
            <p className="text-[11px] text-blue-700 mt-0.5 tabular-nums">
              {t('konstrukcja.za_kwp', { stawka: String(Math.round(wynik.zlZaKwp)).replace('.', ','), waluta: KRAJ.symbol, kwp })}
            </p>
          </div>
        ) : (
          <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
            <p className="text-xs text-gray-500">
              {t('konstrukcja.podaj_moc')}
            </p>
          </div>
        )}
      </div>

      <p className="text-[11px] text-gray-400 leading-relaxed mt-3">
        {t('konstrukcja.zastrzezenie')}
      </p>
    </div>
  );
};

export default KrokKonstrukcji;
